/**
 * Transport for the Onboarding surface (spec §9.4, §10.4, §11).
 *
 * Deliberately not `api/client.js`: that wrapper collapses every failure into
 * `new Error(message)` and loses the HTTP status. This screen has to tell
 * "offline" from "session expired" from "no permission" from "the plan came
 * back empty" (spec §11), and each of those renders a different recovery.
 *
 * The errors thrown here carry `status`, which is exactly what
 * `components/states/status.js#classifyError` reads — the shared state layer
 * does the classifying, this module only makes sure the evidence survives.
 */

/** Error carrying the HTTP status and a machine-readable code. */
export class OnboardingApiError extends Error {
  constructor(message, status = 0, code = 'internal_error') {
    super(message);
    this.name = 'OnboardingApiError';
    this.status = status;
    this.code = code;
  }
}

function codeFromStatus(status) {
  if (status === 401) return 'session_expired';
  if (status === 403) return 'permission_denied';
  if (status === 402) return 'app_not_licensed';
  if (status === 423) return 'tenant_suspended';
  if (status === 404) return 'not_found';
  return 'internal_error';
}

async function getJson(url, { token, signal } = {}) {
  let response;
  try {
    response = await fetch(url, {
      signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    // fetch only rejects on a transport failure — that is offline or DNS.
    // The message is preserved because `classifyError` reads it when there is
    // no status code to read ("Failed to fetch" → the offline state).
    throw new OnboardingApiError(err?.message || 'network error', 0, 'network_error');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new OnboardingApiError(
      body.error || `HTTP ${response.status}`,
      response.status,
      body.code || codeFromStatus(response.status)
    );
  }
  return response.json();
}

/** Paths the current user may see. Learners get only group-assigned ones. */
export function fetchAssignedPaths(options) {
  return getJson('/api/learning/paths', options);
}

/** One path plus its ordered modules and this user's per-module counts. */
export function fetchPathDetail(pathId, options) {
  return getJson(`/api/learning/paths/${encodeURIComponent(pathId)}`, options);
}

/** Onboarding-origin missions, optionally scoped to a workspace. */
export function fetchOnboardingMissions({ workspacePath, ...options } = {}) {
  const query = new URLSearchParams({ status: 'onboarding', limit: '200' });
  if (workspacePath) query.set('workspace', workspacePath);
  return getJson(`/api/missions?${query.toString()}`, options);
}

/** LLM-planned path for the connected knowledge source. Slow (up to ~2 min). */
export function fetchGeneratedPlan(sessionId, options) {
  return getJson(`/api/ckb/onboarding-plan?sessionId=${encodeURIComponent(sessionId)}`, options);
}
